import { useTheme } from '@emotion/react';
import { type FunctionComponent, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { Button } from '~/ui/core/button/Button';

import type { JobItemDetails } from '../../api/use-job-item-details';
import { useJobItemVersions } from '../../api/use-job-item-versions';
import { getJobTypeTranslation, useReviewAction } from '../../hooks/use-review-action';
import { ButtonContainer, Container, Text, TextContainer, variants } from './notification-bar-styled';
import { getNewerVersions, getVersionsFromCurrent, hasReviewedOrPublishedVersion } from './notification-version-utils';

export const ReviewerNotification: FunctionComponent<{
    readonly architectureId: string;
    readonly item: JobItemDetails;
    readonly itemType: 'job-level' | 'job-role';
}> = ({ architectureId, item, itemType }) => {
    const { translate } = useContext(ApplicationContext);
    const { data: versions } = useJobItemVersions({
        architectureId: item.architectureId,
        itemId: item.id,
        itemType: item.type,
    });
    const theme = useTheme();
    const { handleReview } = useReviewAction({ architectureId, item, itemType });

    if (!item.actions?.canAssignReviewer) {
        return null;
    }

    const jobType = getJobTypeTranslation(itemType, translate);

    const versionsFromCurrent = getVersionsFromCurrent(versions, item.versionInfo?.version);
    const newerVersions = getNewerVersions(versions, item.versionInfo?.version);

    const hasNewerReviewedOrPublishedVersion = hasReviewedOrPublishedVersion(newerVersions);

    if (hasNewerReviewedOrPublishedVersion) {
        return null;
    }

    const hasPreviousReviewedOrPublishedVersion = hasReviewedOrPublishedVersion(versionsFromCurrent);

    const notificationKey = hasPreviousReviewedOrPublishedVersion
        ? 'pages.job-architecture-v2.job-role-review-new-version-notification'
        : 'pages.job-architecture-v2.job-role-review-notification';

    return (
        <Container
            key="reviewer"
            animate={variants.animate}
            exit={variants.exit}
            initial={variants.initial}
            style={{ backgroundColor: theme.colors.info[25], borderColor: theme.colors.info[300] }}
        >
            <TextContainer style={{ color: theme.colors.info[600] }}>
                <Text>{translate(notificationKey, { jobType })}</Text>
            </TextContainer>
            <ButtonContainer>
                <Button variant="primary" onClick={handleReview}>
                    {translate('pages.job-architecture.mark-as-reviewed')}
                </Button>
            </ButtonContainer>
        </Container>
    );
};
